import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { suppliers as suppliersApi, roi } from "@/api";
import http from "@/api";
import { useAuth } from "@/auth";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import PillarRadar from "@/components/PillarRadar";
import TierBadge from "@/components/TierBadge";
import DigitalSeal from "@/components/DigitalSeal";
import EvidencePanel from "@/components/EvidencePanel";
import WorkflowPanel from "@/components/WorkflowPanel";
import ChangeRequestPanel from "@/components/ChangeRequestPanel";
import InvitationModal from "@/components/InvitationModal";
import SupplierDocumentsPanel from "@/components/SupplierDocumentsPanel";
import SupplierTargetsPanel from "@/components/SupplierTargetsPanel";
import RecognitionPanel from "@/components/RecognitionPanel";
import { sustainability } from "@/api";
import { ArrowLeft, Pencil, Trash2, Link2, FileText, Download, Leaf } from "lucide-react";

import { PILLARS, TIER_META } from "@/constants";

export default function SupplierDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [history, setHistory] = useState(null);
  const [badge, setBadge] = useState(null);
  const [err, setErr] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [supplierFlags, setSupplierFlags] = useState({});
  const [showInvite, setShowInvite] = useState(false);
  const [roiProjects, setRoiProjects] = useState(null);

  useEffect(() => {
    roi.list({ supplier_id: id }).then((r) => setRoiProjects(r.items || [])).catch(() => setRoiProjects(null));
  }, [id, reloadKey]);

  useEffect(() => {
    Promise.all([
      suppliersApi.get(id),
      suppliersApi.history(id),
      suppliersApi.badge(id),
    ]).then(([s, h, b]) => {
      setSupplier(s); setHistory(h); setBadge(b);
    }).catch((e) => setErr(e.response?.data?.detail || e.message));
    http.get("/program/supplier-flags").then((r) => setSupplierFlags(r.data?.flags || {})).catch(() => {});
  }, [id, reloadKey]);

  const trend = useMemo(() => {
    if (!history) return [];
    return history.evaluations.map((e) => ({
      period: e.period,
      score: e.score,
      ...e.pillars,
    }));
  }, [history]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-20 text-center"><div className="font-display text-3xl mb-2">Sin acceso</div><div className="text-[var(--ink-soft)]">{err}</div></div>;
  if (!supplier) return <div className="px-6 py-20 text-center font-hand text-2xl text-[var(--lala-blue)]">Cargando…</div>;

  const latest = supplier.latest_evaluation;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <Link to="/proveedores" className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mb-4" data-testid="back-link">
        <ArrowLeft size={14} /> Volver a proveedores
      </Link>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 sketch-border bg-[var(--cream-soft)] p-7">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="font-hand text-lg text-[var(--lala-blue)]">{supplier.category}{supplier.rfc && <span className="ml-2 text-[var(--ink-soft)]">RFC {supplier.rfc}</span>}{supplier.sap_id && <span className="ml-2 text-[var(--ink-soft)]" data-testid="supplier-sap-id">· SAP {supplier.sap_id}</span>}</div>
              <h1 className="font-display text-4xl mt-1" data-testid="supplier-name">{supplier.name}</h1>
              {supplier.description && <p className="text-[var(--ink-soft)] mt-2 max-w-xl">{supplier.description}</p>}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {/* <EcoBadge badge={supplier.eco_badge} /> */}
                <TierBadge tier={supplier.current_tier} size="lg" />
              </div>
              {user?.role === "admin" && (
                <div className="flex gap-1.5 flex-wrap justify-end">
                  <Link to={`/admin/proveedores/${id}/editar`} className="sketch-pill bg-[var(--cream)] px-2.5 py-1 text-xs font-semibold text-[var(--lala-blue)]" data-testid="edit-supplier-btn">
                    <Pencil size={12} className="inline mr-1" /> Editar
                  </Link>
                  <button onClick={() => setShowInvite(true)} className="sketch-pill bg-[var(--cream)] px-2.5 py-1 text-xs font-semibold text-[var(--lala-blue)]" data-testid="invite-supplier-btn">
                    <Link2 size={12} className="inline mr-1" /> Invitar
                  </button>
                  {/* <button
                    onClick={async () => {
                      const cur = supplier.eco_badge;
                      const next = !(cur?.earned && cur?.reason === "manual_grant");
                      const note = window.prompt(next ? "Nota para otorgar el badge Eco (opcional)" : "Nota para revocar el badge Eco (opcional)", "") || "";
                      try {
                        await sustainability.set(id, { enabled: next, note });
                        setReloadKey((k) => k + 1);
                      } catch (e) { setErr(e.response?.data?.detail || e.message); }
                    }}
                    className="sketch-pill bg-[var(--cream)] px-2.5 py-1 text-xs font-semibold text-[var(--lala-green)] border-[var(--lala-green)]"
                    style={{ boxShadow: "3px 3px 0 0 #009639" }}
                    data-testid="toggle-eco-badge-btn"
                    title="Otorgar/revocar badge Eco manualmente. Si no se ha tocado, se calcula automáticamente del pilar Sostenibilidad."
                  >
                    <Leaf size={12} className="inline mr-1" />
                    {supplier.eco_badge?.earned ? "Revocar Eco" : "Otorgar Eco"}
                  </button> */}
                  <button
                    onClick={async () => {
                      if (!window.confirm(`¿Eliminar a ${supplier.name}? Se borrarán también sus evaluaciones.`)) return;
                      try { await suppliersApi.remove(id); nav("/proveedores"); } catch (e) { setErr(e.message); }
                    }}
                    className="sketch-pill bg-[var(--cream)] px-2.5 py-1 text-xs font-semibold text-red-700 border-red-600"
                    style={{ boxShadow: "3px 3px 0 0 #B91C1C" }}
                    data-testid="delete-supplier-btn"
                  >
                    <Trash2 size={12} className="inline mr-1" /> Eliminar
                  </button>
                </div>
              )}
              {(user?.role === "admin" || user?.role === "evaluator" || user?.role === "stakeholder") && (
                <button
                  onClick={async () => {
                    try {
                      const res = await http.get(`/suppliers/${id}/executive-report`, { responseType: "blob" });
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `Lala_Elite_Reporte_${(supplier.name || "proveedor").replace(/\s+/g, "_")}.pdf`;
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (e) {
                      window.alert("No se pudo generar el reporte: " + (e.response?.data?.detail || e.message));
                    }
                  }}
                  data-testid="download-exec-report-btn"
                  className="sketch-pill bg-[var(--lala-red,#E0162B)] text-[var(--cream)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:opacity-90"
                  title="Descargar reporte ejecutivo PDF del último período publicado"
                >
                  <FileText size={12} /> Reporte ejecutivo <Download size={12} />
                </button>
              )}
              <ChangeRequestPanel supplierId={id} supplierName={supplier?.name} role={user?.role} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Score actual</div>
              <div className="font-display text-4xl text-[var(--lala-blue)]" data-testid="supplier-score">{supplier.current_score?.toFixed?.(2) ?? "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Último período</div>
              <div className="font-display text-2xl">{supplier.last_period || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Contacto</div>
              <div className="text-sm font-semibold break-all">{supplier.contact_email || "—"}</div>
            </div>
          </div>
        </div>
        <div>{badge && <DigitalSeal badge={badge} />}</div>
      </div>

      {latest && (
        <div className="mb-6">
          <WorkflowPanel evaluation={latest} onChanged={() => setReloadKey((k) => k + 1)} />
        </div>
      )}

      <div className="mb-6">
        <RecognitionPanel supplierId={id} />
      </div>

      {roiProjects !== null && (
        <div className="sketch-border bg-white p-6 mb-6" data-testid="supplier-roi-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-hand text-xl text-[var(--lala-blue)]">palancas de valor</div>
              <h3 className="font-display text-2xl">Valor económico generado con Lala</h3>
            </div>
            <Link to="/impacto-economico" className="text-xs font-bold text-[var(--lala-blue)] hover:underline" data-testid="supplier-roi-link">
              Ver módulo de impacto económico →
            </Link>
          </div>
          {roiProjects.length === 0 ? (
            <p className="text-sm text-[var(--ink-soft)] mt-2" data-testid="supplier-roi-empty">
              Este proveedor aún no tiene proyectos de valor registrados. Regístralos en el módulo de Impacto económico.
            </p>
          ) : (
            <>
              <div className="grid sm:grid-cols-3 gap-4 mt-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-soft)]">Validado</div>
                  <div className="font-display text-2xl" style={{ color: "#009639" }} data-testid="supplier-roi-validado">
                    {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(roiProjects.filter((p) => p.status === "validado").reduce((a, p) => a + p.amount_mxn, 0))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-soft)]">Estimado (por validar)</div>
                  <div className="font-display text-2xl text-amber-600" data-testid="supplier-roi-estimado">
                    {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(roiProjects.filter((p) => p.status === "estimado").reduce((a, p) => a + p.amount_mxn, 0))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-soft)]">Proyectos</div>
                  <div className="font-display text-2xl" data-testid="supplier-roi-count">{roiProjects.length}</div>
                </div>
              </div>
              <div className="mt-3 space-y-1">
                {roiProjects.slice(0, 4).map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm border-b border-[var(--line)]/50 py-1" data-testid={`supplier-roi-project-${p.id}`}>
                    <span className="truncate mr-2">{p.title} <span className="text-[10px] text-[var(--ink-soft)]">· {p.lever}</span></span>
                    <span className="font-bold whitespace-nowrap">{new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(p.amount_mxn)}
                      <span className={`ml-2 text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 ${p.status === "validado" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {latest ? (
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <div className="sketch-border bg-[var(--cream-soft)] p-6">
            <div className="font-hand text-xl text-[var(--lala-blue)]">scorecard {latest.period}</div>
            <h3 className="font-display text-2xl mb-2">Seis pilares</h3>
            <PillarRadar pillars={latest.pillars} />
          </div>
          <div className="sketch-border bg-[var(--cream-soft)] p-6">
            <div className="font-hand text-xl text-[var(--lala-blue)]">desglose por pilar</div>
            <div className="space-y-3 mt-3">
              {PILLARS.map((p) => {
                const raw = latest.pillars[p.key];
                const hasData = raw !== null && raw !== undefined;
                const v = hasData ? raw : 0;
                const avg = history?.pillar_averages?.[p.key];
                return (
                  <div key={p.key} data-testid={`pillar-row-${p.key}`}>
                    <div className="flex justify-between text-sm font-semibold">
                      <span>{p.label}</span>
                      <span>
                        {hasData
                          ? <span className="text-[var(--lala-blue)]">{v.toFixed(2)}</span>
                          : <span className="text-[var(--ink-soft)]" data-testid={`pillar-no-data-${p.key}`}>Sin dato</span>}
                        {avg != null && <span className="text-[var(--ink-soft)] font-normal text-xs ml-2">prom: {avg.toFixed(2)}</span>}
                      </span>
                    </div>
                    <div className="h-2.5 mt-1 rounded-full bg-[var(--cream-deep)] relative">
                      <div className="h-2.5 rounded-full" style={{ width: `${(v / 10) * 100}%`, background: "var(--lala-blue)" }} />
                      {avg != null && (
                        <div className="absolute top-[-2px] h-3.5 w-0.5 bg-[var(--ink)]" style={{ left: `${(avg / 10) * 100}%` }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {latest.notes && (
              <div className="mt-4 p-3 rounded-lg bg-[var(--cream)] border border-[var(--line)] text-sm">
                <strong>Notas:</strong> {latest.notes}
              </div>
            )}
            {latest.sub_kpis && latest.sub_kpis_snapshot && (
              <details className="mt-4" data-testid="sub-kpis-breakdown">
                <summary className="cursor-pointer text-sm font-semibold text-[var(--lala-blue)]">
                  Ver desglose por sub-KPI ↓
                </summary>
                <div className="mt-3 space-y-3">
                  {PILLARS.map((p) => {
                    const defs = latest.sub_kpis_snapshot[p.key];
                    const vals = latest.sub_kpis[p.key];
                    if (!defs || !vals) return null;
                    return (
                      <div key={p.key} className="p-3 rounded-lg bg-[var(--cream)] border border-[var(--line)]">
                        <div className="text-xs uppercase tracking-wider text-[var(--lala-blue)] font-bold">{p.label}</div>
                        <ul className="mt-2 space-y-1 text-xs">
                          {defs.map((d) => (
                            <li key={d.key} className="flex justify-between">
                              <span>{d.label} <span className="text-[var(--ink-soft)]">· {d.weight}%</span></span>
                              <span className="font-semibold">{Number(vals[d.key] ?? 0).toFixed(2)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </details>
            )}
            {latest.weights_snapshot && (
              <div className="mt-3 text-xs text-[var(--ink-soft)]" data-testid="weights-snapshot">
                <strong>Pesos aplicados:</strong>{" "}
                {PILLARS.map((p, i) => (
                  <span key={p.key}>{i > 0 && " · "}{p.label} {Number(latest.weights_snapshot[p.key] || 0).toFixed(0)}%</span>
                ))}
              </div>
            )}
            <div className="text-xs text-[var(--ink-soft)] mt-3">
              Evaluado por {latest.evaluator_name} · {new Date(latest.created_at).toLocaleDateString("es-MX")}
            </div>
          </div>
        </div>
      ) : (
        <div className="sketch-border bg-[var(--cream-soft)] p-6 text-center text-[var(--ink-soft)]">
          Aún no hay evaluaciones registradas para este proveedor.
        </div>
      )}

      {/* History */}
      {trend.length > 1 && (
        <div className="sketch-border bg-[var(--cream-soft)] p-6">
          <div className="font-hand text-xl text-[var(--lala-blue)]">histórico · análisis de tendencia</div>
          <h3 className="font-display text-2xl mb-3">Evolución mensual</h3>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer width="100%" height={320} minWidth={200} minHeight={160} initialDimension={{ width: 520, height: 320 }}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#D9CFB9" />
                <XAxis dataKey="period" stroke="#11142B" />
                <YAxis domain={[5, 10]} stroke="#11142B" />
                <Tooltip contentStyle={{ background: "#F8F2E6", border: "1.5px solid #0033AD", borderRadius: 12 }} />
                <Legend />
                <Line type="monotone" dataKey="score" stroke="#0033AD" strokeWidth={3} dot={{ r: 5 }} name="Promedio" />
                {PILLARS.map((p, i) => (
                  <Line key={p.key} type="monotone" dataKey={p.key} stroke={["#4E5BD8","#D4A437","#9BA4B5","#B87333","#11142B","#3F4566"][i]} strokeWidth={1.4} dot={false} name={p.label} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-[var(--ink-soft)] mt-3">
            La línea negra en cada pilar indica el promedio histórico, lo que permite distinguir incidencias puntuales vs. patrones recurrentes.
          </p>
        </div>
      )}

      {latest && supplierFlags.supplier_self_assessment_enabled && (
        <div className="mt-6">
          <EvidencePanel evaluationId={latest.id} />
        </div>
      )}

      <div className="mt-6">
        <SupplierTargetsPanel
          supplierId={supplier.id}
          canEdit={user?.role === "admin" || user?.role === "evaluator"}
        />
      </div>

      <div className="mt-6">
      </div>

      {supplierFlags.supplier_document_upload_enabled && (user?.role === "admin" || user?.role === "evaluator" || user?.role === "stakeholder") && (
        <div className="mt-6">
          <SupplierDocumentsPanel supplierId={supplier.id} />
        </div>
      )}

      {showInvite && (
        <InvitationModal supplierId={supplier.id} supplierName={supplier.name} onClose={() => setShowInvite(false)} />
      )}
    </div>
  );
}
